/**
 * Hide quantity controls for Grip Trial line items in Kaching cart.
 * Also emits a lightweight `wg:cart:mutated` event when Shopify cart AJAX
 * endpoints succeed, so Trial Builder can unlock after items are removed.
 */
(() => {
  const TRIAL_PROPERTY = '_wg_trial';
  const ITEM_SELECTOR = '.kaching-cart-item[data-line-key], .kaching-cart-item[data-key]';
  const QUANTITY_SELECTOR = '.kaching-cart-item__quantity-controls';
  const TRIAL_CLASS = 'is-wg-trial';
  const STYLE_ID = 'wg-trial-qty-style';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i;

  let trialKeys = new Set(Array.isArray(window.__wgTrialLineKeys) ? window.__wgTrialLineKeys : []);
  let refreshTimer = 0;
  let kachingItemCount = -1;

  function isTrialValue(value) {
    if (value == null || value === '') return false;
    const normalized = String(value).toLowerCase();
    return normalized !== 'false' && normalized !== '0';
  }

  function isFullCart(cart) {
    return Boolean(cart && typeof cart.item_count === 'number' && Array.isArray(cart.items));
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartMutationUrl(url) {
    return CART_MUTATION.test(String(url || ''));
  }

  function lineKeyFromItem(item) {
    return item?.key || item?.id || '';
  }

  function keysFromCart(cart) {
    return new Set(
      (cart?.items || [])
        .filter((item) => isTrialValue(item?.properties?.[TRIAL_PROPERTY]))
        .map(lineKeyFromItem)
        .filter(Boolean)
        .map(String)
    );
  }

  function cssAttrValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function updateStyle() {
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }

    const keys = [...trialKeys];
    if (!keys.length) {
      style.textContent = '';
      return;
    }

    const selectors = keys.flatMap((key) => {
      const escaped = cssAttrValue(key);
      return [
        `.kaching-cart-item[data-line-key="${escaped}"] ${QUANTITY_SELECTOR}`,
        `.kaching-cart-item[data-key="${escaped}"] ${QUANTITY_SELECTOR}`
      ];
    });

    style.textContent = `${selectors.join(',')}{display:none!important}`;
  }

  function applyClasses() {
    document.querySelectorAll(ITEM_SELECTOR).forEach((el) => {
      const key = el.getAttribute('data-line-key') || el.getAttribute('data-key');
      const isTrial = trialKeys.has(String(key));
      el.classList.toggle(TRIAL_CLASS, isTrial);

      const controls = el.querySelector(QUANTITY_SELECTOR);
      if (controls) {
        controls.hidden = isTrial;
        controls.setAttribute('aria-hidden', isTrial ? 'true' : 'false');
      }
    });
  }

  function applyCart(cart) {
    if (!isFullCart(cart)) return;
    trialKeys = keysFromCart(cart);
    window.__wgTrialLineKeys = [...trialKeys];
    updateStyle();
    applyClasses();
    document.dispatchEvent(new CustomEvent('wg:cart:mutated', { detail: { cart } }));
  }

  async function refreshFromCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
    if (!response.ok) return;
    applyCart(await response.json());
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshFromCart().catch(() => {});
    }, 200);
  }

  function patchCartNetwork() {
    if (window.__wgCartWatchPatched) return;
    window.__wgCartWatchPatched = true;

    const originalFetch = window.fetch;
    window.fetch = function patchedFetch(input, init) {
      return originalFetch.apply(this, arguments).then((response) => {
        if (response && response.ok && isCartMutationUrl(requestUrl(input))) {
          scheduleRefresh();
        }
        return response;
      });
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      this.__wgCartUrl = String(url || '');
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function patchedSend() {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300 && isCartMutationUrl(this.__wgCartUrl)) {
          scheduleRefresh();
        }
      });
      return originalSend.apply(this, arguments);
    };
  }

  function observeCart() {
    const root = document.getElementById('kaching-cart') || document.body;
    kachingItemCount = document.querySelectorAll('.kaching-cart-item').length;
    const observer = new MutationObserver(() => {
      applyClasses();
      const count = document.querySelectorAll('.kaching-cart-item').length;
      if (count !== kachingItemCount) {
        kachingItemCount = count;
        scheduleRefresh();
      }
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  document.addEventListener('cart:change', (event) => {
    if (isFullCart(event?.detail?.cart)) {
      applyCart(event.detail.cart);
      return;
    }
    scheduleRefresh();
  });
  document.addEventListener('kaching:cart:updated', scheduleRefresh);
  document.addEventListener('kachingCart:updated', scheduleRefresh);
  window.addEventListener('pageshow', scheduleRefresh);

  patchCartNetwork();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      updateStyle();
      applyClasses();
      observeCart();
    });
  } else {
    updateStyle();
    applyClasses();
    observeCart();
  }
})();
